import { defineMessages, useIntl } from 'react-intl';

import { format } from 'date-fns/format';
import { isToday } from 'date-fns/isToday';
import { isTomorrow } from 'date-fns/isTomorrow';

import { type AddressType } from '@sg/garnish';

import { getStartAndEndDates } from '@order/utils';

/**
 * Returns the wanted time as "ASAP", "Today", "Tomorrow", or "Friday"
 */
export const useDate = (wantedTime: Date, isWantedTimeAsap: boolean) => {
  const { formatMessage } = useIntl();

  if (isWantedTimeAsap && isToday(new Date(wantedTime))) {
    return formatMessage(messages.asap);
  }

  if (isToday(new Date(wantedTime))) {
    return formatMessage(messages.today);
  }

  if (isTomorrow(new Date(wantedTime))) {
    return formatMessage(messages.tomorrow);
  }

  return format(new Date(wantedTime), 'EEEE');
};

/**
 * Returns the wanted time in 12-hour format: "3:30PM" / "12:30AM"
 * Or in a time range format: "3:30PM - 4:30PM"
 */
export const getTime = (wantedTime: Date, channel: AddressType) => {
  if (channel === 'delivery') {
    const { startDate, endDate } = getStartAndEndDates(wantedTime);

    return `${format(startDate, 'h:mm')}-${format(endDate, 'h:mma')}`;
  }

  return format(new Date(wantedTime), 'h:mma');
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  asap: {
    defaultMessage: 'ASAP',
    description: 'Bag | Order playback | ASAP',
  },
  today: {
    defaultMessage: 'Today',
    description: 'Bag | Order playback | Today',
  },
  tomorrow: {
    defaultMessage: 'Tomorrow',
    description: 'Bag | Order playback | Tomorrow',
  },
});
